/**
 * Listy płac (spec 01 §8) — dwa pod-widoki: lista okresów i szczegół naliczenia.
 *
 * Ekran powstał w Etapie 6 jako stan pusty z notą „WKRÓTCE"; teraz stoi na
 * realnym silniku (P5) i pokazuje wyłącznie kwoty policzone z akt. Czego silnik
 * nie wie, tego nie podstawia — mówi to w karcie „Nietypowe elementy" [D19].
 *
 * Akcje zmieniające listę (przelicz, zatwierdź, zamknij, nowa lista) widzi
 * wyłącznie rola płacowa; dział HR ma sam podgląd, więc przyciski u niego nie
 * istnieją, zamiast być wyszarzone [D28].
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  ChevronRight,
  EmptyState,
  FormField,
  InfoBanner,
  Input,
  Modal,
  Select,
  Spinner,
  StatCard,
  StatusPill,
  Wallet,
  Warning,
  cx,
  useToast,
  type StatusPillTone,
} from '@k1p4/ui'
import { formatDate, formatPLN } from '@k1p4/i18n'
import {
  PAYROLL_COMPONENT_UNITS,
  type PayrollPeriodStatus,
  type PayrollWarningLevel,
} from '@k1p4/shared'
import { api, ApiError, type PayrollWarningItem, apiMonth } from '../../api/client'
import { PAYROLL_EXTRA_KINDS, type PayrollComponentKind } from '@k1p4/shared'
import { useHasPermission } from '../../auth/AuthProvider'
import { pluralUnit } from '../portal/units'
import { useFormat } from '../../format'
import styles from './Payroll.module.css'

const STATUS_TONE: Record<PayrollPeriodStatus, StatusPillTone> = {
  przygotowanie: 'neutral',
  kalkulacja: 'pink',
  weryfikacja: 'warning',
  zatwierdzona: 'success',
  zamknięta: 'neutral',
}

/** Nazwa okresu: „Marzec 2026" albo etykieta dodatkowej listy w tym miesiącu. */
function usePeriodName(): (month: string, label: string | null) => string {
  const fmt = useFormat()
  return (month, label) => {
    const name = fmt.month(month, 'long')
    return label ? `${name} · ${label}` : name
  }
}

/** Miesiąc bieżący w formacie rrrr-mm — domyślna wartość formularza nowej listy. */
function currentMonth(): string {
  return apiMonth()
}

type Translate = ReturnType<typeof useTranslation>['t']

/** „1 osoba", „2 osoby", „5 osób" — liczba osób z odmianą, nie „2 osób" (grupa 62). */
function peopleLabel(t: Translate, language: string, count: number): string {
  return `${count} ${pluralUnit(language, count, t, 'person')}`
}

/** Liczba jednostek składnika z mianem: „5,5 h", „1 dzień", „12 dni" (grupa 62). */
function unitsLabel(
  t: Translate,
  language: string,
  kind: PayrollComponentKind,
  units: number,
): string {
  const number = new Intl.NumberFormat(language.startsWith('en') ? 'en-GB' : 'pl-PL', {
    maximumFractionDigits: 2,
  }).format(units)
  return PAYROLL_COMPONENT_UNITS[kind] === 'days'
    ? `${number} ${pluralUnit(language, units, t, 'day')}`
    : `${number} ${t('portal.units.hour')}`
}

function warningText(
  t: Translate,
  warning: PayrollWarningItem,
  language: string,
  periodMonth: string,
  formatMonth: (month: string) => string,
): { title: string; description: string } {
  /*
   * Serwer oddaje KLUCZ i parametry, nie zdanie — kwoty formatujemy tutaj,
   * żeby polski i angielski dostały właściwy separator tysięcy i „zł".
   */
  const params: Record<string, string | number> = { ...warning.params }
  for (const [key, value] of Object.entries(warning.params)) {
    if (key.endsWith('Gr') && typeof value === 'number') {
      params[key.slice(0, -2)] = formatPLN(value)
    }
    /* Stawki w punktach bazowych pokazujemy w procentach, z przecinkiem w PL. */
    if (key.endsWith('Bp') && typeof value === 'number') {
      params[key.slice(0, -2)] = new Intl.NumberFormat(language === 'en' ? 'en-GB' : 'pl-PL', {
        maximumFractionDigits: 2,
      }).format(value / 100)
    }
  }
  /*
   * Miesiąc w parametrze („rrrr-mm”) w zapisie okresu, nie w ISO (fala 9b,
   * grupa 151): „Okres czasu pracy 09/2026 nie jest zamknięty”, jak na pulpicie.
   */
  for (const [key, value] of Object.entries(warning.params)) {
    if (typeof value === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(value)) params[key] = formatMonth(value)
  }
  if (warning.employeeName) params.employee = warning.employeeName
  /*
   * Opisy liczone w dniach odmieniają „dzień/dni” po `count` (fala 9, grupa 93)
   * — serwer niesie liczbę jako `days`.
   */
  if (typeof warning.params.days === 'number' && warning.params.count === undefined) {
    params.count = warning.params.days
  }
  if (typeof warning.params.count === 'number') {
    params.people = peopleLabel(t, language, warning.params.count)
  }
  /* Ograniczenie potrącenia z nazwą granicy, a nie surowym kodem. */
  if (typeof warning.params.reason === 'string') {
    params.reasonLabel = t(`hrPayroll.limitReasons.${warning.params.reason}`, {
      defaultValue: t('hrPayroll.limitReasons.granica_laczna'),
    })
  }
  /*
   * Kierunek odchylenia z kwot, porównanie z miesiącem poprzedniej listy
   * (makieta 01 §120: „wyższe o 38 % niż w maju"). Ostrzeżenia sprzed tej
   * poprawki nie mają miesiąca — dostają „niż w poprzednim okresie".
   */
  if (warning.kind === 'odchylenie_wynagrodzenia') {
    const previous = Number(warning.params.previousGr ?? 0)
    const current = Number(warning.params.currentGr ?? 0)
    params.direction = t(current >= previous ? 'hrPayroll.deviation.up' : 'hrPayroll.deviation.down')
    const previousMonth = warning.params.previousMonth
    params.when =
      typeof previousMonth !== 'string'
        ? t('hrPayroll.deviation.previousPeriod')
        : previousMonth === periodMonth
          ? t('hrPayroll.deviation.sameMonth')
          : t(`hrPayroll.monthsIn.${Number(previousMonth.slice(5, 7))}`)
  }
  /*
   * Ostrzeżenia zapisane przed Falą 1 nie niosą stawek PPK, a lista
   * zatwierdzona się nie przelicza — opis bez stawek zamiast „{{employeeRate}}".
   */
  const legacyPpk =
    warning.kind === 'brak_deklaracji_ppk' &&
    (warning.params.employeeRateBp === undefined || warning.params.employerRateBp === undefined)
  /* Nieznany rodzaj (np. nowszy serwer) — zdanie zastępcze, nigdy surowy klucz (grupa 174). */
  const title = t(`hrPayroll.warnings.${warning.kind}`, {
    ...params,
    defaultValue: t('hrPayroll.warningUnknown'),
  })
  /*
   * Ostrzeżenie o osobie mówi, o kogo chodzi, nawet gdy zdanie jej nie wymienia:
   * „Bartosz Kruk — potrącenie ograniczone…", z małej litery jak pozostałe
   * tytuły z osobą (skrótów w rodzaju „ZUS" nie ruszamy).
   */
  const lowered =
    !language.startsWith('en') && title.length > 1 && title[1] === title[1]!.toLowerCase()
      ? title[0]!.toLowerCase() + title.slice(1)
      : title
  return {
    title:
      warning.employeeName && !title.includes(warning.employeeName)
        ? t('hrPayroll.warningForEmployee', { employee: warning.employeeName, title: lowered })
        : title,
    description: t(
      legacyPpk ? 'hrPayroll.warnings.brak_deklaracji_ppkDescLegacy' : `hrPayroll.warnings.${warning.kind}Desc`,
      { ...params, defaultValue: '' },
    ),
  }
}

const LEVEL_LABEL: Record<PayrollWarningLevel, string> = {
  high: 'hrPayroll.warningLevel.high',
  info: 'hrPayroll.warningLevel.info',
}

// ---------------------------------------------------------------------------
// 8a. Lista okresów
// ---------------------------------------------------------------------------

export function Payroll() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const periodName = usePeriodName()
  const canManage = useHasPermission('Płace : Zatwierdzanie')
  const canViewParameters = useHasPermission('Płace : Podgląd')
  const ids = { month: useId(), payoutDate: useId(), label: useId() }

  const [formOpen, setFormOpen] = useState(false)
  const [form, setForm] = useState({ month: currentMonth(), payoutDate: '', label: '' })
  const [error, setError] = useState<string | null>(null)

  const periods = useQuery({ queryKey: ['payroll'], queryFn: () => api.payrollPeriods() })

  const create = useMutation({
    mutationFn: () =>
      api.createPayrollPeriod({
        month: form.month,
        ...(form.payoutDate ? { payoutDate: form.payoutDate } : {}),
        ...(form.label.trim() ? { label: form.label.trim() } : {}),
      }),
    onSuccess: (created) => {
      setFormOpen(false)
      setForm({ month: currentMonth(), payoutDate: '', label: '' })
      void queryClient.invalidateQueries({ queryKey: ['payroll'] })
      void navigate(`/app/payroll/${created.id}`)
    },
    onError: (cause) => {
      if (cause instanceof ApiError && cause.code === 'PERIOD_EXISTS') {
        setError('hrPayroll.errorExists')
      } else if (cause instanceof ApiError && cause.code === 'PARAMETERS_MISSING') {
        setError('hrPayroll.errorParameters')
      } else {
        setError('hrPayroll.errorGeneric')
      }
    },
  })

  if (periods.isPending) return <Spinner label={t('common.loading')} />
  if (periods.isError) {
    return <EmptyState title={t('common.error')} description={t('common.retry')} />
  }

  const items = periods.data.items

  return (
    <>
      <div className={styles.header}>
        <p className={styles.lead}>{t('hrPayroll.lead')}</p>
        {/*
          Parametry roku zatwierdza rola płacowa, która nie ma Konfiguracji —
          stałe wejście z Listy płac (grupa 84, D94).
        */}
        <div className={styles.headerActions}>
          {canViewParameters ? (
            <Button variant="secondary" onClick={() => void navigate('/app/settings/payroll')}>
              {t('hrPayroll.parametersLink')}
            </Button>
          ) : null}
          {canManage ? (
            <Button
              onClick={() => {
                setError(null)
                setFormOpen(true)
              }}
            >
              {t('hrPayroll.add')}
            </Button>
          ) : null}
        </div>
      </div>

      {error !== null && !formOpen ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t(error)}
          {error === 'hrPayroll.errorParameters' && canViewParameters ? (
            <>
              {' '}
              <Button size="sm" variant="secondary" onClick={() => void navigate('/app/settings/payroll')}>
                {t('hrPayroll.parametersCta')}
              </Button>
            </>
          ) : null}
        </InfoBanner>
      ) : null}

      {items.length === 0 ? (
        <EmptyState
          icon={<Wallet size={28} />}
          title={t('hrPayroll.empty')}
          description={t(canManage ? 'hrPayroll.emptyBodyManage' : 'hrPayroll.emptyBody')}
        />
      ) : (
        <Card pad="flush" className={styles.list}>
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              className={styles.row}
              onClick={() => void navigate(`/app/payroll/${item.id}`)}
            >
              <span className={styles.rowIcon} aria-hidden="true">
                <Wallet size={18} />
              </span>
              <span className={styles.rowBody}>
                <span className={styles.rowTitle}>{periodName(item.month, item.label)}</span>
                <span className={styles.rowMeta}>
                  {item.calculatedAt === null
                    ? t('hrPayroll.notCalculated')
                    : item.warningCount > 0
                      ? t('hrPayroll.warningsToCheck', { count: item.warningCount })
                      : t('hrPayroll.noWarnings')}
                </span>
              </span>
              <span className={styles.rowPeople}>
                {peopleLabel(t, i18n.language, item.peopleCount)}
              </span>
              <span className={styles.rowCost}>
                {item.employerCostGr === null ? '—' : formatPLN(item.employerCostGr)}
              </span>
              <StatusPill tone={STATUS_TONE[item.status]}>
                {t(`hrPayroll.status.${item.status}`)}
              </StatusPill>
              <span className={styles.rowChevron} aria-hidden="true">
                <ChevronRight size={18} />
              </span>
            </button>
          ))}
        </Card>
      )}

      {formOpen ? (
        <Modal open title={t('hrPayroll.add')} onClose={() => setFormOpen(false)}>
          {error !== null ? (
            <InfoBanner tone="danger" className={styles.banner}>
              {t(error)}
              {error === 'hrPayroll.errorParameters' && canViewParameters ? (
                <>
                  {' '}
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => void navigate('/app/settings/payroll')}
                  >
                    {t('hrPayroll.parametersCta')}
                  </Button>
                </>
              ) : null}
            </InfoBanner>
          ) : null}
          <FormField
            label={t('hrPayroll.form.monthWorked')}
            htmlFor={ids.month}
            hint={t('hrPayroll.form.monthWorkedHint')}
          >
            <Input
              id={ids.month}
              type="month"
              value={form.month}
              onChange={(event) => setForm({ ...form, month: event.target.value })}
            />
          </FormField>
          <FormField
            label={t('hrPayroll.form.payoutDate')}
            htmlFor={ids.payoutDate}
            hint={t('hrPayroll.form.payoutDateHint')}
          >
            <Input
              id={ids.payoutDate}
              type="date"
              value={form.payoutDate}
              onChange={(event) => setForm({ ...form, payoutDate: event.target.value })}
            />
          </FormField>
          <FormField
            label={t('hrPayroll.form.label')}
            htmlFor={ids.label}
            hint={t('hrPayroll.form.labelHint')}
          >
            <Input
              id={ids.label}
              value={form.label}
              onChange={(event) => setForm({ ...form, label: event.target.value })}
            />
          </FormField>
          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setFormOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => {
                setError(null)
                create.mutate()
              }}
              disabled={create.isPending || !/^\d{4}-\d{2}$/.test(form.month)}
            >
              {t('hrPayroll.form.submit')}
            </Button>
          </div>
        </Modal>
      ) : null}
    </>
  )
}

// ---------------------------------------------------------------------------
// 8b. Szczegół okresu
// ---------------------------------------------------------------------------

export function PayrollPeriod() {
  const { t, i18n } = useTranslation()
  const fmt = useFormat()
  const navigate = useNavigate()
  const toast = useToast()
  const queryClient = useQueryClient()
  const periodName = usePeriodName()
  const canManage = useHasPermission('Płace : Zatwierdzanie')
  const canViewParameters = useHasPermission('Płace : Podgląd')
  const { id } = useParams<{ id: string }>()

  const [error, setError] = useState<string | null>(null)
  const [extraOpen, setExtraOpen] = useState(false)
  const [extraForm, setExtraForm] = useState({
    employeeId: '',
    kind: 'premia' as PayrollComponentKind,
    amount: '',
    note: '',
  })
  const [reopenOpen, setReopenOpen] = useState(false)
  const [reopenReason, setReopenReason] = useState('')
  /*
   * Podsumowanie pobranego pliku przelewów (Etap 34): ile zleceń, w tym ile
   * potrąceń, i czego zabrakło. Plik zostaje plikiem — bank nie zniesie w nim
   * komentarzy — więc liczby przychodzą nagłówkami i pokazujemy je tutaj.
   */
  const [transfers, setTransfers] = useState<Awaited<
    ReturnType<typeof api.downloadTransfers>
  > | null>(null)
  const reasonId = useId()
  const extraIds = {
    employee: useId(),
    kind: useId(),
    amount: useId(),
    note: useId(),
  }

  const period = useQuery({
    queryKey: ['payroll', id],
    queryFn: () => api.payrollPeriod(id!),
    enabled: id !== undefined,
  })

  /*
   * „Otwórz ponownie" (Etap 13): zamknięta lista wraca do weryfikacji. Powód
   * jest obowiązkowy, bo idzie do dziennika audytu, a przekazany komplet
   * deklaracji trzeba będzie skorygować — mówimy o tym od razu.
   */
  const reopen = useMutation({
    mutationFn: () => api.reopenPayrollPeriod(id!, reopenReason.trim()),
    onSuccess: (result) => {
      setReopenOpen(false)
      setReopenReason('')
      toast.show(
        result.correctionRequired
          ? t('hrPayroll.toast.reopenCorrection', {
              month: result.settlementMonth.split('-').toReversed().join('/'),
            })
          : t('hrPayroll.toast.reopen'),
        { tone: 'info' },
      )
      void queryClient.invalidateQueries({ queryKey: ['payroll'] })
    },
    onError: (cause) => {
      setReopenOpen(false)
      setError(cause instanceof ApiError ? 'hrPayroll.errorReopen' : 'hrPayroll.errorGeneric')
    },
  })

  const download = useMutation({
    mutationFn: () => api.downloadTransfers(id!),
    onSuccess: (summary) => {
      setTransfers(summary)
      /* Dzień pierwszego przelewu zapisuje się przy tytule — odświeżamy akta. */
      void queryClient.invalidateQueries({ queryKey: ['garnishments'] })
    },
    onError: (cause) => {
      /*
       * Każdy brak danych płatnika ma zdanie — „spróbuj ponownie" przy braku
       * mikrorachunku albo NIP-u kazało ponawiać bez skutku (przegląd Fali 1).
       */
      const code = cause instanceof ApiError ? cause.code : null
      setError(
        code === 'NO_BANK_ACCOUNT' || code === 'NO_ZUS_ACCOUNT' || code === 'NO_TAX_ACCOUNT'
          ? 'hrPayroll.transfersNoAccount'
          : code === 'NO_NIP'
            ? 'hrPayroll.transfersNoNip'
            : 'hrPayroll.transfersError',
      )
    },
  })

  const act = useMutation({
    mutationFn: (action: 'calculate' | 'approve' | 'close') => api.payrollAction(id!, action),
    onSuccess: (_result, action) => {
      toast.show(t(`hrPayroll.toast.${action}`), { tone: 'success' })
      void queryClient.invalidateQueries({ queryKey: ['payroll'] })
    },
    onError: (cause) => {
      if (cause instanceof ApiError && cause.code === 'PERIOD_LOCKED') {
        setError('hrPayroll.errorLocked')
      } else if (cause instanceof ApiError && cause.code === 'PARAMETERS_MISSING') {
        setError('hrPayroll.errorParameters')
      } else {
        setError('hrPayroll.errorGeneric')
      }
    },
  })

  /*
   * Jednorazowe składniki (Etap 14) są WEJŚCIEM naliczenia, nie wierszem
   * wyniku — po dopisaniu trzeba przeliczyć okres, i ekran mówi to wprost
   * zamiast pokazywać kwotę, której nie ma jeszcze na pasku.
   */
  const addExtra = useMutation({
    mutationFn: () =>
      api.addPayrollExtra(id!, {
        employeeId: extraForm.employeeId,
        kind: extraForm.kind,
        amountGr: Math.round(Number(extraForm.amount) * 100),
        ...(extraForm.note.trim() ? { note: extraForm.note.trim() } : {}),
      }),
    onSuccess: () => {
      setExtraOpen(false)
      setExtraForm({ employeeId: '', kind: 'premia', amount: '', note: '' })
      toast.show(t('hrPayroll.extras.added'), { tone: 'success' })
      void queryClient.invalidateQueries({ queryKey: ['payroll'] })
    },
    onError: (cause) => {
      setError(
        cause instanceof ApiError && cause.code === 'PERIOD_LOCKED'
          ? 'hrPayroll.errorLocked'
          : 'hrPayroll.errorGeneric',
      )
    },
  })

  const removeExtra = useMutation({
    mutationFn: (extraId: string) => api.removePayrollExtra(extraId),
    onSuccess: () => {
      toast.show(t('hrPayroll.extras.removed'), { tone: 'info' })
      void queryClient.invalidateQueries({ queryKey: ['payroll'] })
    },
  })

  if (period.isPending) return <Spinner label={t('common.loading')} />
  if (period.isError || !period.data) {
    return <EmptyState title={t('common.error')} description={t('common.retry')} />
  }

  const data = period.data
  const name = periodName(data.month, data.label)
  const calculated = data.calculatedAt !== null
  const deviation =
    data.previousEmployerCostGr !== null &&
    data.previousEmployerCostGr > 0 &&
    data.employerCostGr !== null
      ? Math.round(
          ((data.employerCostGr - data.previousEmployerCostGr) / data.previousEmployerCostGr) *
            1000,
        ) / 10
      : null

  return (
    <>
      <button type="button" className={styles.back} onClick={() => void navigate('/app/payroll')}>
        {t('hrPayroll.back')}
      </button>

      <div className={styles.detailHead}>
        <div>
          <h2 className={styles.detailTitle}>{name}</h2>
          <p className={styles.detailMeta}>
            {calculated
              ? t('hrPayroll.calculatedAt', { date: formatDate(data.calculatedAt!, 'dot') })
              : t('hrPayroll.notCalculated')}
          </p>
          {/*
            Dwa zegary (ADR-0013): nazwa okresu mówi o miesiącu przepracowanym,
            a podatek i składki idą za miesiącem wypłaty. Pokazujemy oba, bo
            księgowa szuka listy pod miesiącem rozliczeniowym.
          */}
          <p className={styles.detailMeta}>
            {t('hrPayroll.payoutDate', { date: formatDate(data.payoutDate, 'dot') })} ·{' '}
            {t('hrPayroll.settlementMonth', {
              month: data.settlementMonth.split('-').toReversed().join('/'),
            })}
          </p>
        </div>
        <div className={styles.detailActions}>
          <StatusPill tone={STATUS_TONE[data.status]}>
            {t(`hrPayroll.status.${data.status}`)}
          </StatusPill>
          {canManage && data.status !== 'zatwierdzona' && data.status !== 'zamknięta' ? (
            <Button
              variant="secondary"
              onClick={() => {
                setError(null)
                act.mutate('calculate')
              }}
              disabled={act.isPending}
            >
              {t('hrPayroll.recalculate')}
            </Button>
          ) : null}
          {canManage && data.status === 'weryfikacja' && calculated ? (
            <Button
              onClick={() => {
                setError(null)
                act.mutate('approve')
              }}
              disabled={act.isPending}
            >
              {t('hrPayroll.approve')}
            </Button>
          ) : null}
          {canManage && data.status === 'zatwierdzona' ? (
            <Button
              onClick={() => {
                setError(null)
                act.mutate('close')
              }}
              disabled={act.isPending}
            >
              {t('hrPayroll.close')}
            </Button>
          ) : null}
          {/*
            Przelewy powstają z listy ZAMKNIĘTEJ (Etap 19): lista w weryfikacji
            może się jeszcze zmienić, a przelewu nie da się cofnąć klikiem.
          */}
          {canManage && data.status === 'zamknięta' ? (
            <Button
              variant="secondary"
              onClick={() => {
                setError(null)
                setTransfers(null)
                download.mutate()
              }}
              disabled={download.isPending}
            >
              {download.isPending ? (
                <Spinner label={t('hrPayroll.transfersPending')} />
              ) : (
                t('hrPayroll.transfers')
              )}
            </Button>
          ) : null}
          {canManage && (data.status === 'zatwierdzona' || data.status === 'zamknięta') ? (
            <Button
              variant="secondary"
              onClick={() => {
                setError(null)
                setReopenOpen(true)
              }}
              disabled={reopen.isPending}
            >
              {t('hrPayroll.reopen')}
            </Button>
          ) : null}
        </div>
      </div>

      {data.reopenCount > 0 ? (
        <InfoBanner tone="warning" className={styles.banner}>
          {t('hrPayroll.reopened', {
            count: data.reopenCount,
            reason: data.reopenReason ?? '',
          })}
        </InfoBanner>
      ) : null}

      {error !== null ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t(error)}
          {error === 'hrPayroll.errorParameters' && canViewParameters ? (
            <>
              {' '}
              <Button size="sm" variant="secondary" onClick={() => void navigate('/app/settings/payroll')}>
                {t('hrPayroll.parametersCta')}
              </Button>
            </>
          ) : null}
        </InfoBanner>
      ) : null}

      {/*
        Plik przelewów już wyszedł — mówimy, co w nim jest i czego zabrakło.
        Brak rachunku to nie awaria, tylko zadanie dla działu płac: przelewu
        nie da się złożyć z numeru, którego nikt nie podał (Etap 34).
      */}
      {transfers !== null ? (
        <InfoBanner
          tone={
            transfers.missing +
              transfers.payeesMissing +
              transfers.ppkUnpaidGr +
              transfers.ppkUndeclaredGr +
              transfers.zusBenefitsUnappliedGr >
            0
              ? 'warning'
              : 'info'
          }
          className={styles.banner}
        >
          {t('hrPayroll.transfersDone', {
            orders: transfers.orders,
            garnishments: transfers.garnishments,
          })}
          {transfers.missing > 0 ? ` ${t('hrPayroll.transfersMissing', { count: transfers.missing })}` : ''}
          {transfers.payeesMissing > 0
            ? ` ${t('hrPayroll.transfersPayeesMissing', { count: transfers.payeesMissing })}`
            : ''}
          {/* Blok V, luka PPK i wpłaty osób bez deklaracji — liczbą, nie ciszą (#1, #85). */}
          {transfers.zusBenefitsGr > 0
            ? ` ${t('hrPayroll.transfersZusBenefits', { amount: formatPLN(transfers.zusBenefitsGr) })}`
            : ''}
          {transfers.zusBenefitsUnappliedGr > 0
            ? ` ${t('hrPayroll.transfersZusBenefitsUnapplied', {
                amount: formatPLN(transfers.zusBenefitsUnappliedGr),
              })}`
            : ''}
          {transfers.ppkUnpaidGr > 0
            ? ` ${t('hrPayroll.transfersPpkMissing', {
                amount: formatPLN(transfers.ppkUnpaidGr),
                date: transfers.ppkDeadline ? formatDate(transfers.ppkDeadline, 'dot') : '—',
              })}`
            : ''}
          {transfers.ppkUndeclaredGr > 0
            ? ` ${t('hrPayroll.transfersPpkUndeclared', {
                amount: formatPLN(transfers.ppkUndeclaredGr),
              })}`
            : ''}
        </InfoBanner>
      ) : null}

      {!data.parametersConfirmed && calculated ? (
        <InfoBanner tone="warning" className={styles.banner}>
          {t('hrPayroll.parametersUnconfirmed', { year: data.parametersYear ?? '' })}
          {canViewParameters ? (
            <>
              {' '}
              <Button size="sm" variant="secondary" onClick={() => void navigate('/app/settings/payroll')}>
                {t('hrPayroll.parametersCta')}
              </Button>
            </>
          ) : null}
        </InfoBanner>
      ) : null}

      {!calculated ? (
        <EmptyState
          icon={<Wallet size={28} />}
          title={t('hrPayroll.notCalculated')}
          description={t(canManage ? 'hrPayroll.notCalculatedManage' : 'hrPayroll.notCalculatedBody')}
        />
      ) : (
        <>
          <div className={styles.kpiGrid}>
            <StatCard
              label={t('hrPayroll.kpi.cost')}
              value={formatPLN(data.employerCostGr ?? 0)}
              note={
                deviation === null
                  ? t('hrPayroll.kpi.costNoteFirst')
                  : t('hrPayroll.kpi.costNote', {
                      /* Liczba — przecinek i znak „+" dokłada formater języka (fala 9, grupa 92). */
                      value: deviation,
                    })
              }
            />
            <StatCard
              label={t('hrPayroll.kpi.people')}
              value={data.peopleCount}
              note={t(
                (data.employmentSplit.dzielo ?? 0) > 0
                  ? 'hrPayroll.kpi.peopleNoteDzielo'
                  : 'hrPayroll.kpi.peopleNote',
                {
                  uop: data.employmentSplit.uop,
                  other: data.employmentSplit.zlecenie + data.employmentSplit.b2b,
                  dzielo: `${data.employmentSplit.dzielo ?? 0} ${pluralUnit(
                    i18n.language,
                    data.employmentSplit.dzielo ?? 0,
                    t,
                    'specificTask',
                  )}`,
                },
              )}
            />
            <StatCard
              label={t('hrPayroll.kpi.warnings')}
              value={data.warningCount}
              note={t('hrPayroll.kpi.warningsNote')}
              highlighted={data.warnings.some((warning) => warning.level === 'high')}
            />
          </div>

          <div className={styles.columns}>
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>{t('hrPayroll.componentsTitle')}</h3>
              <Card pad="flush" className={styles.list}>
                {data.componentTotals.map((total) => (
                  <div key={total.kind} className={styles.componentRow}>
                    <span className={styles.componentBody}>
                      <span className={styles.rowTitle}>
                        {t(`hrPayroll.components.${total.kind}`)}
                      </span>
                      <span className={styles.rowMeta}>
                        {total.units === null
                          ? peopleLabel(t, i18n.language, total.peopleCount)
                          : t('hrPayroll.componentUnits', {
                              people: peopleLabel(t, i18n.language, total.peopleCount),
                              units: unitsLabel(t, i18n.language, total.kind, total.units),
                            })}
                      </span>
                    </span>
                    <span className={styles.rowCost}>{formatPLN(total.amountGr)}</span>
                  </div>
                ))}
                <div className={styles.componentRow}>
                  <span className={styles.componentBody}>
                    <span className={styles.rowTitle}>{t('hrPayroll.employerContributions')}</span>
                    <span className={styles.rowMeta}>
                      {t('hrPayroll.employerContributionsNote')}
                    </span>
                  </span>
                  <span className={styles.rowCost}>
                    {formatPLN(data.socialEmployerGr + data.ppkEmployerGr)}
                  </span>
                </div>
                <div className={cx(styles.componentRow, styles.componentTotal)}>
                  <span className={styles.componentBody}>
                    <span className={styles.rowTitle}>{t('hrPayroll.totalCost')}</span>
                  </span>
                  <span className={styles.rowCost}>{formatPLN(data.employerCostGr ?? 0)}</span>
                </div>
              </Card>

              <div className={styles.sectionHead}>
                <h3 className={styles.sectionTitle}>{t('hrPayroll.extras.title')}</h3>
                {canManage && data.status !== 'zatwierdzona' && data.status !== 'zamknięta' ? (
                  <Button size="sm" variant="secondary" onClick={() => setExtraOpen(true)}>
                    {t('hrPayroll.extras.add')}
                  </Button>
                ) : null}
              </div>
              {data.extras.length === 0 ? (
                <p className={styles.asideNote}>{t('hrPayroll.extras.empty')}</p>
              ) : (
                <Card pad="flush" className={styles.list}>
                  {data.extras.map((extra) => (
                    <div key={extra.id} className={styles.componentRow}>
                      <span className={styles.componentBody}>
                        <span className={styles.rowTitle}>
                          {extra.employeeName} - {t(`hrPayroll.components.${extra.kind}`)}
                        </span>
                        <span className={styles.rowMeta}>
                          {extra.note ?? t('hrPayroll.extras.noNote')}
                        </span>
                      </span>
                      <span className={styles.rowCost}>{formatPLN(extra.amountGr)}</span>
                      {canManage && data.status !== 'zatwierdzona' && data.status !== 'zamknięta' ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={removeExtra.isPending}
                          onClick={() => removeExtra.mutate(extra.id)}
                        >
                          {t('hrPayroll.extras.remove')}
                        </Button>
                      ) : null}
                    </div>
                  ))}
                </Card>
              )}

              <h3 className={styles.sectionTitle}>{t('hrPayroll.payslipsTitle')}</h3>
              <Card pad="flush" className={styles.list}>
                <div className={styles.payslipHead}>
                  <span>{t('hrPayroll.payslipColumns.employee')}</span>
                  <span>{t('hrPayroll.payslipColumns.gross')}</span>
                  <span>{t('hrPayroll.payslipColumns.net')}</span>
                  <span>{t('hrPayroll.payslipColumns.cost')}</span>
                </div>
                {data.payslips.map((slip) => (
                  <div key={slip.id} className={styles.payslipRow}>
                    <span className={styles.rowTitle}>{slip.employeeName}</span>
                    <span className={styles.rowCost}>{formatPLN(slip.grossGr)}</span>
                    <span className={styles.rowCost}>{formatPLN(slip.netGr)}</span>
                    <span className={styles.rowCost}>{formatPLN(slip.employerCostGr)}</span>
                  </div>
                ))}
              </Card>
            </section>

            <aside className={styles.aside}>
              <h3 className={styles.sectionTitle}>{t('hrPayroll.warningsTitle')}</h3>
              <p className={styles.asideNote}>{t('hrPayroll.warningsSubtitle')}</p>
              {data.warnings.length === 0 ? (
                <EmptyState title={t('hrPayroll.noWarnings')} />
              ) : (
                <div className={styles.warnings}>
                  {data.warnings.map((warning) => {
                    const text = warningText(t, warning, i18n.language, data.month, (month) => fmt.month(month, 'period'))
                    return (
                      <Card
                        key={warning.id}
                        pad="md"
                        className={cx(
                          styles.warning,
                          warning.level === 'high' ? styles.warningHigh : styles.warningInfo,
                        )}
                      >
                        <div className={styles.warningHead}>
                          <span className={styles.warningIcon} aria-hidden="true">
                            <Warning size={16} />
                          </span>
                          <span className={styles.warningChip}>{t(LEVEL_LABEL[warning.level])}</span>
                        </div>
                        <p className={styles.warningTitle}>{text.title}</p>
                        {text.description === '' ? null : (
                          <p className={styles.warningDesc}>{text.description}</p>
                        )}
                      </Card>
                    )
                  })}
                </div>
              )}
            </aside>
          </div>
        </>
      )}

      <Modal
        open={extraOpen}
        title={t('hrPayroll.extras.add')}
        onClose={() => setExtraOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setExtraOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              disabled={
                addExtra.isPending ||
                extraForm.employeeId === '' ||
                !(Number(extraForm.amount) > 0)
              }
              onClick={() => addExtra.mutate()}
            >
              {t('hrPayroll.extras.submit')}
            </Button>
          </>
        }
      >
        <p className={styles.modalNote}>{t('hrPayroll.extras.hint')}</p>
        <FormField label={t('hrPayroll.extras.employee')} htmlFor={extraIds.employee}>
          <Select
            id={extraIds.employee}
            value={extraForm.employeeId}
            onChange={(event) => setExtraForm({ ...extraForm, employeeId: event.target.value })}
          >
            <option value="">{t('hrPayroll.extras.employeePlaceholder')}</option>
            {data.payslips.map((slip) => (
              <option key={slip.employeeId} value={slip.employeeId}>
                {slip.employeeName}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label={t('hrPayroll.extras.kind')} htmlFor={extraIds.kind}>
          <Select
            id={extraIds.kind}
            value={extraForm.kind}
            onChange={(event) =>
              setExtraForm({ ...extraForm, kind: event.target.value as PayrollComponentKind })
            }
          >
            {PAYROLL_EXTRA_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {t(`hrPayroll.components.${kind}`)}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField
          label={t('hrPayroll.extras.amount')}
          htmlFor={extraIds.amount}
          hint={t('hrPayroll.extras.amountHint')}
        >
          <Input
            id={extraIds.amount}
            inputMode="decimal"
            value={extraForm.amount}
            onChange={(event) => setExtraForm({ ...extraForm, amount: event.target.value })}
          />
        </FormField>
        <FormField label={t('hrPayroll.extras.note')} htmlFor={extraIds.note}>
          <Input
            id={extraIds.note}
            value={extraForm.note}
            onChange={(event) => setExtraForm({ ...extraForm, note: event.target.value })}
          />
        </FormField>
      </Modal>

      <Modal
        open={reopenOpen}
        title={t('hrPayroll.reopen')}
        onClose={() => setReopenOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setReopenOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => reopen.mutate()}
              disabled={reopen.isPending || reopenReason.trim().length < 5}
            >
              {t('hrPayroll.reopenConfirm')}
            </Button>
          </>
        }
      >
        <p className={styles.modalNote}>{t('hrPayroll.reopenHint')}</p>
        <FormField
          label={t('hrPayroll.reopenReason')}
          htmlFor={reasonId}
          hint={t('hrPayroll.reopenReasonHint')}
        >
          <textarea
            id={reasonId}
            className={styles.textarea}
            rows={3}
            value={reopenReason}
            onChange={(event) => setReopenReason(event.target.value)}
          />
        </FormField>
      </Modal>
    </>
  )
}
